import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck, Phone, Mail, Clock, Shield, Zap, MapPin, ArrowRight,
  CheckCircle2, Star, ChevronDown, Car, Gauge, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1761993600321-757e086491c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxjYXIlMjB0cmFuc3BvcnQlMjB2ZWhpY2xlJTIwZGVsaXZlcnklMjB0cnVja3xlbnwwfHx8fDE3NzY0NDgxMjN8MA&ixlib=rb-4.1.0&q=85";
const CARRIER_IMG = "https://images.unsplash.com/photo-1761917904658-2a9ecb84a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjYXIlMjB0cmFuc3BvcnQlMjB2ZWhpY2xlJTIwZGVsaXZlcnklMjB0cnVja3xlbnwwfHx8fDE3NzY0NDgxMjN8MA&ixlib=rb-4.1.0&q=85";
const LONDON_IMG = "https://images.unsplash.com/photo-1764416166626-7f146bd8e36d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxMb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwdmFuJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc3NjQ0NjgxNXww&ixlib=rb-4.1.0&q=85";

const FAQ_DATA = [
  {
    q: "How much does car transport cost in London?",
    a: "We provide instant, transparent quotes for car transport in seconds — calculated from your pickup and delivery postcodes plus your vehicle. Submit your postcodes in the quote calculator for an exact price with no hidden surcharges."
  },
  {
    q: "Can you transport a non-running vehicle?",
    a: "Yes, we can transport non-running, broken-down, and salvage vehicles across London and the UK. Our drivers use professional loading equipment to safely get your vehicle onto the transporter without causing further damage."
  },
  {
    q: "Is my car insured during transport?",
    a: "Absolutely. Every vehicle we transport is covered by our comprehensive goods-in-transit insurance from pickup to delivery. You can rest assured that your car is fully protected throughout the journey."
  },
  {
    q: "Can you collect a car the same day?",
    a: "Yes. We offer same day car transport in London and surrounding areas. Our 24/7 availability means we can often arrange collection within hours of your booking. Call us on +44 7757 133163 for urgent requests."
  },
  {
    q: "Do you transport motorcycles and vans as well?",
    a: "We transport all types of vehicles including cars, motorcycles, vans, classic cars, prestige vehicles, and even caravans. Whatever you need moved, we have the right equipment and experience to handle it safely."
  },
  {
    q: "Can you transport my car from London to another city?",
    a: "Yes, we provide nationwide vehicle transport. Whether you need your car moved from London to Manchester, Birmingham, Edinburgh, or any other UK destination, we cover the entire country with the same reliable service."
  }
];

const AREAS = [
  "Central London", "East London", "West London", "North London", "South London",
  "Hackney", "Camden", "Westminster", "Kensington", "Chelsea",
  "Croydon", "Bromley", "Ealing", "Hounslow", "Richmond",
  "Wandsworth", "Lambeth", "Southwark", "Tower Hamlets", "Barnet",
  "Enfield", "Harrow", "Brent", "Haringey", "Newham"
];

function FAQItem({ question, answer }) {
  return (
    <details className="group border border-slate-200 rounded-lg" data-testid="faq-item">
      <summary className="flex items-center justify-between cursor-pointer p-5 text-left font-semibold text-slate-900 hover:text-orange-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
        {question}
        <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
      </summary>
      <div className="px-5 pb-5 text-slate-600 leading-relaxed">{answer}</div>
    </details>
  );
}

export default function CarTransportLondonPage() {
  const navigate = useNavigate();

  const setMeta = useCallback((attr, attrVal, content) => {
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
    el.setAttribute("content", content);
    el.setAttribute("data-page", "car-transport-london");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageTitle = "Car Transport London | Dedicated, Insured, Same-Day | Transport Anything 247";
    const originalTitle = document.title;
    document.title = pageTitle;
    const titleTimer = setTimeout(() => { document.title = pageTitle; }, 100);

    setMeta("name", "description", "Professional car transport London. Dedicated vehicle collection and delivery, same-day available, fully insured. Instant quotes online. Call +44 7757 133163.");
    setMeta("name", "keywords", "car transport London, vehicle transport London, car delivery service London, car collection London, vehicle shipping London");
    setMeta("property", "og:title", "Car Transport London | Transport Anything 247");
    setMeta("property", "og:description", "Professional car transport service in London. Dedicated same-day vehicle collection and delivery, fully insured, instant quotes online.");
    setMeta("property", "og:type", "website");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://transportanything247.co.uk/services/car-transport-london");
    canonical.setAttribute("data-page", "car-transport-london");

    return () => { document.title = originalTitle; clearTimeout(titleTimer); document.querySelectorAll('[data-page="car-transport-london"]').forEach(el => el.remove()); };
  }, [setMeta]);

  const structuredData = {
    "@context": "https://schema.org", "@type": "Service", "name": "Car Transport London",
    "provider": { "@type": "LocalBusiness", "name": "Transport Anything 247", "telephone": "+447757133163", "email": "customers@trasportanything247.com", "url": "https://transportanything247.co.uk", "areaServed": { "@type": "City", "name": "London" }, "openingHours": "Mo-Su 00:00-23:59" },
    "description": "Professional car transport service in London. Dedicated same-day vehicle collection and delivery, fully insured, with instant transparent quotes online.", "areaServed": "London, United Kingdom", "serviceType": "Car Transport"
  };
  const faqStructuredData = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } }))
  };

  return (
    <div className="min-h-screen" data-testid="car-transport-london-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#calculator" className="nav-link">Get Quote</a>
              <a href="/#services" className="nav-link">Services</a>
              <a href="/#contact" className="nav-link">Contact</a>
              <a href="/blog" className="nav-link">Blog</a>
            </div>
            <a href="tel:+447757133163" className="hidden sm:flex items-center gap-2 text-orange-500 font-medium" data-testid="nav-phone">
              <Phone className="h-4 w-4" /><span>+44 7757 133163</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Cars loaded on a transport carrier in London" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <p className="text-orange-400 font-medium text-sm tracking-wider uppercase mb-4">Professional Vehicle Shipping</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Car Transport <span className="text-orange-500">London</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
              Looking for reliable car transport in London? We collect and deliver vehicles across the capital and nationwide with <strong className="text-white">dedicated, fully-insured</strong> transport. Same-day available, with instant transparent quotes online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-8 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="hero-get-quote-btn">
                Get an Instant Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-medium px-8 py-3 rounded-md transition-colors" data-testid="hero-call-btn">
                <Phone className="h-5 w-5" /> Call Now
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-orange-400" /> Fully Insured</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-400" /> 24/7 Available</span>
              <span className="flex items-center gap-2"><Car className="h-4 w-4 text-orange-400" /> All Vehicle Types</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-slate-900 py-5 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Star, label: "5-Star Rated Transport" },
              { icon: Lock, label: "Goods-in-Transit Insurance" },
              { icon: Gauge, label: "Same Day Collection" },
              { icon: MapPin, label: "London & Nationwide" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                <item.icon className="h-4 w-4 text-orange-500 flex-shrink-0" /><span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Car Transport Services in London
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              From everyday runabouts to prestige sports cars, our experienced team transports vehicles of all types safely across London and the UK.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Car, title: "Standard Car Transport", desc: "Reliable door-to-door transport for saloons, hatchbacks, SUVs, and estate cars. We collect from any London address and deliver anywhere in the UK." },
              { icon: Star, title: "Prestige & Classic Cars", desc: "Specialist handling for high-value, classic, and prestige vehicles. Extra care, protective covers, and experienced drivers who understand your car's worth." },
              { icon: Gauge, title: "Same Day Vehicle Collection", desc: "Urgent car transport when you need it. Our 24/7 service means we can often collect your vehicle within hours of booking, even at weekends." },
              { icon: Truck, title: "Auction & Dealer Transport", desc: "Buying from an auction or dealer? We collect vehicles from sale rooms, showrooms, and private sellers across London and deliver them straight to your door." },
              { icon: Shield, title: "Non-Runner & Salvage", desc: "Broken down or accident-damaged vehicles are no problem. We have the equipment to load non-running cars safely and transport them to garages or salvage yards." },
              { icon: MapPin, title: "Nationwide Vehicle Delivery", desc: "Need your car moved from London to Scotland, Wales, or anywhere in between? We provide reliable long-distance vehicle transport across the entire UK." },
            ].map((service, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                  <service.icon className="h-6 w-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center text-slate-600 text-sm leading-relaxed max-w-2xl mx-auto">
            <p>
              We also provide <a href="/services/man-and-van-london" className="text-orange-500 hover:underline font-medium">man and van</a>, <a href="/services/courier-service-london" className="text-orange-500 hover:underline font-medium">courier</a>, and <a href="/services/furniture-delivery-london" className="text-orange-500 hover:underline font-medium">furniture delivery</a> services across London.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Why Choose Us for Car Transport in London?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Moving a vehicle requires specialist knowledge and equipment. Here is why London drivers and car owners trust Transport Anything 247:
              </p>
              <div className="space-y-4">
                {[
                  { title: "Experienced Vehicle Handlers", desc: "Our drivers are trained in secure vehicle loading and strapping techniques. Your car is in safe hands from collection to delivery." },
                  { title: "Transparent Instant Pricing", desc: "Every quote is calculated transparently from your postcodes and vehicle in seconds. No hidden costs, no inflated quotes — the price you see online is the price you pay." },
                  { title: "Fully Insured Transit", desc: "Comprehensive goods-in-transit insurance covers your vehicle for the entire journey, giving you complete peace of mind." },
                  { title: "Any Vehicle, Any Condition", desc: "Running or not, standard or prestige, we transport them all. Non-runners, salvage, and accident-damaged vehicles are handled with equal professionalism." },
                  { title: "24/7 Availability", desc: "Car breakdowns and urgent transport needs do not follow office hours. That is why we operate around the clock, every day of the year." },
                  { title: "Door-to-Door Service", desc: "We collect from your exact address and deliver to the precise destination. No depot drops, no middlemen, just direct transport." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={CARRIER_IMG} alt="Car carrier truck transporting vehicles on a road" className="w-full h-96 object-cover rounded-xl shadow-lg" />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-xl p-5 shadow-lg hidden md:block" data-testid="car-trust-badge">
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>100%</p>
                <p className="text-sm font-medium">Fully Insured</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 md:py-24 bg-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Car Transport Across All London Boroughs
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We collect and deliver vehicles from every postcode in Greater London. Whether your car is in a residential street, a dealership forecourt, or a commercial car park, we will come to you.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AREAS.map((area, i) => (
                  <span key={i} className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">{area}</span>
                ))}
              </div>
              <p className="text-slate-500 text-sm">
                Need nationwide vehicle transport? We deliver to every UK city. <a href="tel:+447757133163" className="text-orange-500 hover:underline font-medium">Call us</a> for a quote.
              </p>
            </div>
            <div><img src={LONDON_IMG} alt="London street with vehicles" className="w-full h-80 object-cover rounded-xl shadow-lg" /></div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Need Your Car Moved Today?
          </h2>
          <p className="text-slate-300 mb-8 text-base max-w-2xl mx-auto">
            Get an instant car transport quote. Enter your postcodes, select "Vehicle" as the item type, and see your price in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-10 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="cta-get-quote-btn">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-10 py-3 rounded-md transition-colors border border-white/20" data-testid="cta-call-btn">
              <Phone className="h-5 w-5" /> +44 7757 133163
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Car Transport FAQs</h2>
            <p className="text-slate-600 text-base">Common questions about our London car transport service.</p>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((item, i) => (<FAQItem key={i} question={item.q} answer={item.a} />))}
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Trusted Car Transport in London</h2>
          <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed space-y-4">
            <p>Transporting a car in London presents unique challenges. Congestion charges, residents-only parking, low emission zones, and narrow residential streets all make vehicle logistics complicated. That is exactly why you need a specialist car transport service that knows London inside and out.</p>
            <p>At Transport Anything 247, our drivers navigate London every day. They know which routes avoid the worst congestion, where to safely load and unload vehicles, and how to handle everything from compact city cars to large SUVs and prestige sports cars.</p>
            <p>Our pricing is simple and transparent. You receive an instant, dedicated quote based on your postcodes and vehicle, with no call-out fees, no fuel surcharges, and no hidden extras. The price on our quote calculator is the price you pay.</p>
            <p>Whether you have bought a car online that needs collecting, need a vehicle moved to a new address, or require emergency breakdown recovery transport, we are here 24 hours a day, 7 days a week. We also work with dealerships, auction houses, and fleet operators across London for regular vehicle movements.</p>
            <p>Beyond car transport, we offer a full range of services including <a href="/services/man-and-van-london" className="text-orange-500 hover:underline">man and van</a>, <a href="/services/courier-service-london" className="text-orange-500 hover:underline">same day courier</a>, and <a href="/services/furniture-delivery-london" className="text-orange-500 hover:underline">furniture delivery</a> across London and nationwide.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
              </div>
              <p className="text-slate-400 text-sm">Professional car transport and vehicle delivery in London and across the UK. Affordable, reliable, available 24/7.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Our Services</h4>
              <div className="space-y-2">
                <a href="/#calculator" className="footer-link block">Get Quote</a>
                <a href="/services/man-and-van-london" className="footer-link block">Man and Van London</a>
                <a href="/services/car-transport-london" className="footer-link block text-orange-400">Car Transport London</a>
                <a href="/services/courier-service-london" className="footer-link block">Courier Service London</a>
                <a href="/services/furniture-delivery-london" className="footer-link block">Furniture Delivery London</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p>Phone: <a href="tel:+447757133163" className="text-orange-400 hover:text-orange-300">+44 7757 133163</a></p>
                <p>Email: <a href="mailto:customers@trasportanything247.com" className="text-orange-400 hover:text-orange-300">customers@trasportanything247.com</a></p>
                <p>Hours: 24/7 - Always Available</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Transport Anything 247. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
